// frontend-executive/src/pages/settings/SettingsPage.tsx
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSettings } from './hooks/useSettings';
import { SettingsGroup } from './components/SettingsGroup';
import { SettingRow } from './components/SettingRow';
import { ToggleSetting } from './components/ToggleSetting';
import { SelectSetting } from './components/SelectSetting';
import { DangerZone } from './components/DangerZone';
import { useToast } from '../dashboard/hooks/useToast';
import { 
    MONITORING_INTERVALS, 
    AUTO_LOGOUT_OPTIONS, 
    LANGUAGES, 
    TIMEZONES 
} from './constants';
import type { SettingsState } from './types';

// ИСПОЛЬЗУЕМ: типизированный объект иконок
const Icons: Record<string, React.ReactNode> = {
    monitoring: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
    ),
    appearance: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
        </svg>
    ),
    notifications: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
    ),
    security: (
        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
    ),
};

interface SettingsPageProps {
    onThemeChange?: (isDark: boolean) => void;
}

export const SettingsPage: React.FC<SettingsPageProps> = ({ onThemeChange }) => {
    const { state, updateSetting, resetToDefaults, exportSettings, importSettings } = useSettings();
    const { addToast } = useToast();

    useEffect(() => {
        onThemeChange?.(state.darkMode);
        document.documentElement.classList.toggle('dark', state.darkMode);
    }, [state.darkMode, onThemeChange]);

    const handleSettingChange = <K extends keyof SettingsState>(
        key: K,
        value: boolean | string | number
    ) => {
        updateSetting(key, value);
        
        const messages: Record<string, string> = {
            darkMode: value ? 'Тёмная тема включена' : 'Светлая тема включена',
            animations: value ? 'Анимации включены' : 'Анимации выключены',
            pushNotifications: value ? 'Push-уведомления включены' : 'Push-уведомления выключены',
            twoFactor: value ? 'Двухфакторная аутентификация включена' : 'Двухфакторная аутентификация выключена',
        };
        
        if (messages[key]) {
            addToast(messages[key], 'success');
        }
    };

    const handleReset = () => {
        resetToDefaults();
        addToast('Настройки сброшены к значениям по умолчанию', 'success');
    };

    const handleExport = () => {
        exportSettings();
        addToast('Настройки экспортированы', 'success');
    };

    const handleImport = async (file: File) => {
        try {
            await importSettings(file);
            addToast('Настройки импортированы успешно', 'success');
        } catch {
            addToast('Ошибка импорта настроек', 'error');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="pb-8"
        >
            <div className="px-[18px] pt-4 pb-2.5">
                <h1 className="text-[17px] font-semibold tracking-[-0.3px] text-[var(--text)] mb-1">
                    Настройки
                </h1>
                <p className="text-[11px] text-[var(--text-muted)]">
                    Управление системой мониторинга
                </p>
            </div>

            <div className="space-y-4 px-[18px]">
                {/* ИСПОЛЬЗУЕМ: передаём Icons.monitoring */}
                <SettingsGroup label="Мониторинг" icon={Icons.monitoring} delay={0.05}>
                    <SettingRow
                        title="Как часто проверять сайты"
                        description="Чем чаще — тем быстрее узнаете о сбое"
                    >
                        <SelectSetting
                            value={String(state.monitoringInterval)}
                            options={MONITORING_INTERVALS}
                            onChange={(v) => {
                                updateSetting('monitoringInterval', Number(v));
                                addToast(`Интервал проверки изменён`, 'success');
                            }}
                        />
                    </SettingRow>
                    
                    <SettingRow
                        title="Предел медленной загрузки"
                        description="При превышении — приходит предупреждение"
                        last
                    >
                        <span className="text-[13px] font-mono font-medium text-[var(--text)] bg-[var(--bg)] px-3 py-1.5 rounded-lg border border-[var(--border)]">
                            {state.slowLoadThreshold} <span className="text-[var(--text-muted)] text-xs">мс</span>
                        </span>
                    </SettingRow>
                </SettingsGroup>

                {/* ИСПОЛЬЗУЕМ: Icons.appearance */}
                <SettingsGroup label="Внешний вид" icon={Icons.appearance} delay={0.1}>
                    <SettingRow
                        title="Тёмная тема"
                        description="Тёмный фон — удобнее ночью"
                    >
                        <ToggleSetting
                            checked={state.darkMode}
                            onChange={(v) => handleSettingChange('darkMode', v)}
                        />
                    </SettingRow>
                    
                    <SettingRow
                        title="Анимации"
                        description="Плавные переходы между экранами"
                        last
                    >
                        <ToggleSetting
                            checked={state.animations}
                            onChange={(v) => handleSettingChange('animations', v)}
                        />
                    </SettingRow>
                </SettingsGroup>

                {/* ИСПОЛЬЗУЕМ: Icons.notifications */}
                <SettingsGroup label="Уведомления" icon={Icons.notifications} delay={0.15}>
                    <SettingRow
                        title="Push-уведомления"
                        description="Узнавайте о сбоях мгновенно"
                    >
                        <ToggleSetting
                            checked={state.pushNotifications}
                            onChange={(v) => handleSettingChange('pushNotifications', v)}
                        />
                    </SettingRow>
                    
                    <SettingRow
                        title="Email-уведомления"
                        description="Дублирование важных событий на почту"
                    >
                        <ToggleSetting
                            checked={state.emailNotifications}
                            onChange={(v) => handleSettingChange('emailNotifications', v)}
                        />
                    </SettingRow>
                    
                    <SettingRow
                        title="Звуковые оповещения"
                        description="Звук при критических сбоях"
                        last
                    >
                        <ToggleSetting
                            checked={state.soundAlerts}
                            onChange={(v) => handleSettingChange('soundAlerts', v)}
                        />
                    </SettingRow>
                </SettingsGroup>

                <SettingsGroup label="Региональные настройки" delay={0.2}>
                    <SettingRow
                        title="Язык интерфейса"
                        description="Язык отображения системы"
                    >
                        <SelectSetting
                            value={state.language}
                            options={LANGUAGES}
                            onChange={(v) => {
                                updateSetting('language', v);
                                addToast('Язык изменён', 'success');
                            }}
                        />
                    </SettingRow>
                    
                    <SettingRow
                        title="Часовой пояс"
                        description="Для корректного отображения времени"
                        last
                    >
                        <SelectSetting
                            value={state.timezone}
                            options={TIMEZONES}
                            onChange={(v) => {
                                updateSetting('timezone', v);
                                addToast('Часовой пояс изменён', 'success');
                            }}
                        />
                    </SettingRow>
                </SettingsGroup>

                {/* ИСПОЛЬЗУЕМ: Icons.security */}
                <SettingsGroup label="Безопасность" icon={Icons.security} delay={0.25}>
                    <SettingRow
                        title="Автоматический выход"
                        description="По истечении времени бездействия"
                    >
                        <SelectSetting
                            value={String(state.autoLogout)}
                            options={AUTO_LOGOUT_OPTIONS}
                            onChange={(v) => {
                                updateSetting('autoLogout', Number(v));
                                addToast('Настройка автовыхода изменена', 'success');
                            }}
                        />
                    </SettingRow>
                    
                    <SettingRow
                        title="Двухфакторная аутентификация"
                        description="Дополнительный уровень защиты"
                        last
                    >
                        <ToggleSetting
                            checked={state.twoFactor}
                            onChange={(v) => handleSettingChange('twoFactor', v)}
                        />
                    </SettingRow>
                </SettingsGroup>

                <DangerZone
                    onReset={handleReset}
                    onExport={handleExport}
                    onImport={handleImport}
                />

                <div className="pt-4 text-center">
                    <p className="text-[11px] text-[var(--text-muted)]">
                        Сканта v1.0.0
                    </p>
                </div>
            </div>
        </motion.div>
    );
};